<template>
	<basic-block :value="value" :mode="mode" :state.sync="state">
		<template v-slot:main>
			<div class="carousel">
				<div class="carousel-inner" :style="canvasStyle">
					<template v-for="card in value.data.cards">
						<input
							v-if="visibleCardId === card.id"
							:key="`input-${card.id}`"
							class="carousel-open"
							type="radio"
							:id="`carousel-${card.id + 1}`"
							name="carousel"
							aria-hidden="true"
							hidden=""
							:checked="card.id === 0"
						/>
						<div
							v-if="visibleCardId === card.id"
							:id="card.id"
							:key="`item-${card.id}`"
							:class="getTransition"
							class="carousel-item"
						>
							<block-subcanvas
								v-if="mode === RevisionDisplayMode.edit"
								:value="value"
								:blocks="card.blocks"
								:mode="RevisionDisplayMode.edit"
								@create:draft="$emit('create:draft', $event)"
								:blockVariables="blockVariables"
							/>
							<block-subcanvas-readonly
								v-else-if="visibleCardId === card.id"
								:blocks="card.blocks"
								:mode="mode ? mode : RevisionDisplayMode.view"
								@goToBookmarkTarget="$emit('goToBookmarkTarget', $event)"
								@selectSlideById="$emit('selectSlideById', $event)"
								:blockVariables="blockVariables"
								:subcanvasDelay="subcanvasHasDelay ? cardsDelay : undefined"
							/>
						</div>
					</template>

					<label
						:for="`carousel-${visibleCardId + 1}`"
						class="carousel-control prev"
						:style="arrowsStyle"
						@click="handleNavigation('prev')"
						:class="{ disabled: isLeftArrowDisabled }"
					>
						‹
					</label>
					<label
						:for="`carousel-${visibleCardId + 1}`"
						class="carousel-control next"
						:style="arrowsStyle"
						@click="handleNavigation('next')"
						:class="{ disabled: isRightArrowDisabled }"
					>
						›
					</label>

					<div class="carousel-indicators" :id="`indicators-${value.id}`">
						<div v-for="card in value.data.cards" :key="card.id">
							<v-tooltip v-if="displayTooltip" top>
								<template v-slot:activator="{ on }">
									<v-btn
										:for="`carousel-${card.id + 1}`"
										icon
										class="carousel-bullet"
										:class="card.id === visibleCardId ? 'activeBullet' : ''"
										:style="paginatorStyle"
										@click="goToCard(card.id)"
										v-on="on"
									>
										<v-icon>mdi-record</v-icon>
									</v-btn>
								</template>
								<span>{{ card.title }}</span>
							</v-tooltip>

							<v-btn
								v-else
								:for="`carousel-${card.id + 1}`"
								icon
								class="carousel-bullet"
								:class="card.id === visibleCardId ? 'activeBullet' : ''"
								:style="paginatorStyle"
								@click="goToCard(card.id)"
							>
								<v-icon>mdi-record</v-icon>
							</v-btn>
						</div>

						<v-btn v-if="mode === RevisionDisplayMode.edit" flat icon class="carousel-add" @click="addCard">
							<v-icon> mdi-plus </v-icon>
						</v-btn>
					</div>
				</div>
			</div>
		</template>
	</basic-block>
</template>

<script>
import BasicBlock from '../_components/containers/BasicBlock.vue';
import BlockToolbarButton from '../button/BlockToolbarButton.vue';
import BlockSubcanvas from '../_components/containers/BlockSubcanvas.vue';
import BlockSubcanvasReadonly from '../_components/containers/BlockSubcanvasReadonly.vue';
import { BlockCard } from './class.js';
import isEmpty from 'lodash/isEmpty';
import { BlockSelectionState } from '../_models/blockSelectionState';
import { RevisionDisplayMode } from 'plant-common/src/models/revisionDisplayMode';

export default {
	name: 'BlockCarousel',
	inject: ['blocksSelector'],
	props: ['value', 'mode', 'blockVariables'],
	components: {
		BasicBlock,
		BlockToolbarButton,
		BlockSubcanvas,
		BlockSubcanvasReadonly
	},
	data() {
		return {
			visibleCardId: this.value.data.cards[0] ? this.value.data.cards[0].id : -1,
			transition: '',
			state: BlockSelectionState.notSelected,
			cardsVisited: [],
			subcanvasHasDelay: false,
			RevisionDisplayMode
		};
	},
	computed: {
		displayTooltip() {
			return this.value.data.displayCardsTooltip;
		},
		canvasStyle() {
			return {
				'--canvas-color-background': this.value.data.style.canvas.normal.backgroundColor
			};
		},
		arrowsStyle() {
			return {
				'--arrows-color-background': this.value.data.style.arrows.normal.backgroundColor,
				'--arrows-color-arrows': this.value.data.style.arrows.normal.arrowColor,
				'--arrows-color-background-hover': this.value.data.style.arrows.hover.backgroundColor,
				'--arrows-color-arrows-hover': this.value.data.style.arrows.hover.arrowColor
			};
		},
		paginatorStyle() {
			return {
				'--pagination-color-background': this.value.data.style.paginator.normal.backgroundColor,
				'--pagination-color-background-hover': this.value.data.style.paginator.hover.backgroundColor,
				'--pagination-color-background-active': this.value.data.style.paginator.active.backgroundColor
			};
		},
		getTransition() {
			return this.value.data.style.transition.type;
		},
		isLoopNavigation() {
			return this.value.data.navigation.loop;
		},
		currentIndex() {
			return this.value.data.cards.findIndex(card => card.id === this.visibleCardId);
		},
		isLeftArrowDisabled() {
			return !this.isLoopNavigation && this.currentIndex === 0;
		},
		isRightArrowDisabled() {
			return !this.isLoopNavigation && this.currentIndex === this.value.data.cards.length - 1;
		},
		cardsDelay() {
			const animationIn =
				this.value.animations && this.value.animations[0] && this.value.animations[0].name !== 'none'
					? this.value.animations[0]
					: false;
			return animationIn ? parseFloat(animationIn.delay) + this.inheritDelay : undefined;
		},
		inheritDelay() {
			return this.$attrs.subcanvasDelay || 0;
		}
	},
	created() {
		this.subcanvasHasDelay = this.firstTimeLoaded(this.value.data.cards[0]);

		this.setListenerForSelectionChangedEvent();

		this.setAvoidedMigrations();
	},
	mounted() {
		const firstCard = this.value.data.cards[0];
		if (!firstCard) return;

		if (this.canSetCardAsVisited(firstCard.id)) {
			this.setCardAsVisited(firstCard);
		}
		this.updateBulletBackgroundColor();
	},
	watch: {
		visibleCardId: {
			immediate: true,
			handler() {
				if (!isEmpty(this.blocksSelector)) {
					this.blocksSelector.$emit('childrenIdActive', this.visibleCardId);
				}
				this.updateBulletBackgroundColor();
			}
		},
		'value.data.style': {
			deep: true,
			handler() {
				this.updateBulletBackgroundColor();
			}
		},
		'value.data.cards': {
			handler() {
				this.$nextTick(() => {
					this.updateBulletBackgroundColor();
				});
			}
		},
		state: {
			immediate: true,
			handler() {
				if (!isEmpty(this.blocksSelector)) {
					if (this.state === BlockSelectionState.editContent) {
						this.blocksSelector.$emit('childrenIdActive', this.visibleCardId);
					} else if (this.state === BlockSelectionState.notSelected) {
						this.blocksSelector.$emit('childrenIdActive', null);
					}
				}
			}
		}
	},
	methods: {
		setAvoidedMigrations() {
			// AVOIDED MIGRATION
			// Set default navigation property to old carousel block (if it is not set)
			if (this.value?.data?.navigation?.loop === undefined) this.$set(this.value.data, 'navigation', { loop: true });

			// AVOIDED MIGRATION
			// Set default background to old carousel block (if it is not set)
			if (!this.value.data.style.canvas) {
				this.$set(this.value.data.style, 'canvas', {
					normal: {
						backgroundColor: '#ffffff00'
					}
				});
			}
		},

		onSelectionChanged({ block, state }) {
			if (state !== BlockSelectionState.primarySelected) {
				return;
			}

			let cardSelected = this.findCardFromBlockBy(block.id);
			if (cardSelected) {
				this.visibleCardId = cardSelected.id;
			}
		},

		setListenerForSelectionChangedEvent() {
			if (isEmpty(this.blocksSelector)) {
				return;
			}

			this.blocksSelector.$on('selection-changed', this.onSelectionChanged);
		},

		setCardAsVisited(card) {
			this.cardsVisited.push(card.id);
			this.$set(card, 'visited', true);
		},

		canSetCardAsVisited(cardId) {
			return !this.isCardVisited(cardId) && this.mode !== this.RevisionDisplayMode.edit;
		},

		findCardFromBlockBy(id) {
			return this.value.data.cards.find(card => {
				const isCardItself = card.id === id;
				const isInsideCard = card.blocks.some(cardBlock => cardBlock.id === id);
				return isCardItself || isInsideCard;
			});
		},

		isCardVisited(cardId) {
			return this.cardsVisited.includes(cardId);
		},

		goToCard(cardId) {
			this.subcanvasHasDelay = false;
			this.blocksSelector.selectionClear();
			this.blocksSelector.selectionEdit(this.value.id);
			this.visibleCardId = cardId;
			const card = this.value.data.cards.find(card => card.id === cardId);

			if (this.canSetCardAsVisited(cardId)) {
				this.setCardAsVisited(card);
			}
		},

		addCard() {
			this.value.data.cards.push(new BlockCard());
		},

		isIndexBetweenMinMax(index, min, max) {
			return index >= min && index <= max;
		},

		canNavigateForward(index) {
			return index > this.value.data.cards.length - 1 && this.isLoopNavigation;
		},

		canNavigateBackward(index) {
			return index < 0 && this.isLoopNavigation;
		},

		canNavigate(toIndex) {
			return (
				this.isIndexBetweenMinMax(toIndex, 0, this.value.data.cards.length - 1) ||
				this.canNavigateForward(toIndex) ||
				this.canNavigateBackward(toIndex)
			);
		},

		handleNavigation(dir) {
			const targetIndex = dir === 'next' ? this.currentIndex + 1 : this.currentIndex - 1;

			if (!this.canNavigate(targetIndex)) return;

			this.changeCardItem(targetIndex);
		},

		changeCardItem(index) {
			if (index > this.value.data.cards.length - 1) {
				index = 0;
			}
			if (index < 0) {
				index = this.value.data.cards.length - 1;
			}
			this.goToCard(this.value.data.cards[index].id);
		},

		updateBulletBackgroundColor() {
			// Background changed with JS because
			// the parent of active element must be changed and can't be done with CSS
			const carouselIndicators = document.getElementById(`indicators-${this.value.id}`);
			if (!carouselIndicators) {
				return;
			}

			const bullets = carouselIndicators.querySelectorAll('.carousel-bullet');
			bullets.forEach((bullet, index) => {
				if (this.visibleCardId === index) {
					bullet.style.color = this.value.data.style.paginator.active.backgroundColor;
				} else if (this.isCardVisited(this.value.data.cards[index].id) && this.mode !== this.RevisionDisplayMode.edit) {
					bullet.style.color = this.value.data.style.paginator.visited.backgroundColor;
				} else {
					bullet.style.color = this.value.data.style.paginator.normal.backgroundColor;
				}
			});
		},

		firstTimeLoaded(card) {
			if (!card) return false;
			return !this.isCardVisited(card.id) && this.isFirstCard(card);
		},

		isFirstCard(card) {
			return this.value.data.cards[0].id === card.id;
		}
	}
};
</script>

<style>
.carousel {
	position: relative;
	box-shadow: 0 1px 6px rgba(0, 0, 0, 0.64);
	height: 100%;
}

.carousel-inner {
	position: relative;
	overflow: hidden;
	width: 100%;
	height: 100%;
	background-color: var(--canvas-color-background);
}

.carousel-open:checked + .carousel-item {
	position: static;
	opacity: 100;
}

.carousel-item {
	top: 0;
	bottom: 0;
	width: 100vw;
	position: absolute;
}

.carousel-control {
	background: var(--arrows-color-background);
	color: var(--arrows-color-arrows);
	border-radius: 50%;
	font-size: 40px;
	height: 40px;
	line-height: 35px;
	position: absolute;
	top: 50%;
	cursor: pointer;
	-webkit-transform: translate(0, -50%);
	-ms-transform: translate(0, -50%);
	transform: translate(0, -50%);
	text-align: center;
	width: 40px;
	z-index: 10;
}

.carousel-control:hover {
	background: var(--arrows-color-background-hover);
	color: var(--arrows-color-arrows-hover);
}

.carousel-control.prev {
	left: 2%;
}

.carousel-control.next {
	right: 2%;
}

.carousel-indicators {
	margin: 0;
	padding: 0;
	position: absolute;
	bottom: 16px;
	left: 50%;
	transform: translateX(-50%);
	text-align: center;
	z-index: 10;
	display: flex;
	column-gap: 8px;
}

.carousel-bullet {
	color: var(--pagination-color-background);
	cursor: pointer;
	display: block;
	width: 36px;
	margin: 0 !important;
}

.carousel-bullet:hover {
	color: var(--pagination-color-background-hover) !important;
}

.carousel-add {
	margin: 0 !important;
}

.activeBullet {
	color: var(--pagination-color-background-active) !important;
}

.fade-in {
	animation: fade-in linear 1s;
	-webkit-animation: fade-in linear 1s;
}

@keyframes fade-in {
	0% {
		opacity: 0;
	}
	100% {
		opacity: 1;
	}
}

@-webkit-keyframes fade-in {
	0% {
		opacity: 0;
	}
	100% {
		opacity: 1;
	}
}
</style>
